import React from 'react';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { BinIcon, Button, CameraRetakeIcon, IconComponent, IconComponentFunction, IconText, InfoIcon, LockClosedIcon, TickCircleIcon, UploadIcon } from '@motorway/mw-highway-code';
import { LoadingIndicator } from '@motorway/mw-highway-code/alpha';

import { DocumentImageOverlay } from '../../../../../components/forms/ImageUpload/DocumentImageOverlay';
import LocalTexts from '../../DocumentCapture.texts.json';

import { ReviewStatus, UploadDocumentStateLabelProps } from './UploadDocumentsSection.types';

import styles from './UploadDocumentStateLabel.module.scss';

const LocalT = new MDText(LocalTexts);

const retrieveFailureReason = (isBlurred: boolean, invalidReason? : React.ReactNode) : React.ReactNode => {
  if (isBlurred) {
    return LocalT.translate('reviewStatus.uploadStateLabel.lowQualityImage');
  }

  if (invalidReason === LocalT.translate('reviewStatus.uploadStateLabel.duplicatePages')) {
    return LocalT.translate('reviewStatus.uploadStateLabel.duplicatePages');
  }

  return LocalT.translate('reviewStatus.uploadStateLabel.failed');
};

type STATE_LABEL_CONFIG_TYPE = { [key in ReviewStatus]:
  {
    actionIcon?: IconComponentFunction | (() => IconComponent);
    actionIconStyle?: string;
    actionLabel?: React.ReactNode;
    actionVariant?: 'destructive' | 'secondary' | 'tertiary';
    icon?: IconComponentFunction;
    onClick?: () => void;
    style: string;
    text: React.ReactNode;
  }
};

export const UploadDocumentStateLabel = (
  {
    handleDelete,
    handleOnClick,
    hasImage,
    invalidReason,
    isBlurred,
    isImageLoading,
    needsAction = false,
    pageNumberAndLabel,
    reviewStatus,
  }: UploadDocumentStateLabelProps,
) => {
  if (!reviewStatus) {
    return null;
  }

  const STATE_LABEL_CONFIG: STATE_LABEL_CONFIG_TYPE = {
    [ReviewStatus.actionRequired]: {
      actionIcon: CameraRetakeIcon,
      actionIconStyle: styles.retakeButton,
      actionLabel: LocalT.translate('reviewStatus.uploadStateLabel.retake'),
      actionVariant: 'destructive',
      icon: InfoIcon,
      onClick: handleOnClick,
      style: styles.actionRequired,
      text: retrieveFailureReason(isBlurred, invalidReason),
    },
    [ReviewStatus.readyForReview]: {
      actionIcon: BinIcon,
      actionIconStyle: styles.binButton,
      actionLabel: LocalT.translate('reviewStatus.uploadStateLabel.delete'),
      onClick: handleDelete,
      style: styles.binButton,
      text: LocalT.translate('reviewStatus.uploadStateLabel.readyDrft'),
    },
    [ReviewStatus.passed]: {
      actionIcon: LockClosedIcon,
      actionIconStyle: styles.lockIcon,
      actionLabel: LocalT.translate('reviewStatus.uploadStateLabel.locked'),
      actionVariant: 'tertiary',
      icon: TickCircleIcon,
      style: styles.passed,
      text: LocalT.translate('reviewStatus.uploadStateLabel.complete'),
    },
    [ReviewStatus.uploading]: {
      actionIconStyle: styles.uploading,
      icon: UploadIcon,
      style: styles.uploading,
      text: LocalT.translate('reviewStatus.uploadStateLabel.uploading'),
    },
  };

  const config = !hasImage && isImageLoading
    ? STATE_LABEL_CONFIG[ReviewStatus.uploading]
    : STATE_LABEL_CONFIG[reviewStatus];

  const showActionIcon = (reviewStatus === 'actionRequired' || reviewStatus === 'readyForReview' || reviewStatus === 'passed') && !isImageLoading;

  return (
    <>
      {needsAction && <DocumentImageOverlay
        handleOverlayClick={handleOnClick!}
        reviewStatus={reviewStatus}
      />}
      <div className={styles.stateLabel}>
        <div className={styles.pageState} data-state={reviewStatus}>
          <span className={styles.pageNumber}>
            {pageNumberAndLabel}
          </span>
          <div className={config.style}>
            {config.icon ? <IconText icon={config.icon} text={config.text} variant='xsmall--regular' /> : <span>{config.text}</span>}
          </div>
        </div>
        {isImageLoading && <div className={styles.loadingIndicator}><LoadingIndicator /></div> }
        {showActionIcon && <span className={cx(styles.actionButton, config.actionIconStyle)}>
          <Button
            icon={config.actionIcon}
            label={config.actionLabel}
            onClick={config.onClick}
            showLabel={false}
            size="medium"
            type="button"
            variant={config.actionVariant ?? 'secondary'}
          />
        </span>}
      </div>
    </>
  );
};
